import {createSelector} from 'reselect';

import type {Ref as PanelBankConfigRef} from '../../state/views/panelBankConfig/types';
import {type Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {type RootState} from '../../types/redux';

// =============================================================================
// Base Selectors
// =============================================================================
// These are the foundation for all other selectors. They look up a normalized
// part by its ref. Other selectors should build on these rather than accessing
// state.views.parts directly.

export const makePanelBankConfigSelector =
  (panelBankConfigRef: PanelBankConfigRef) => (state: RootState) =>
    state.views.parts[panelBankConfigRef.type][panelBankConfigRef.id];

export const makePanelBankSectionConfigSelector =
  (panelBankSectionConfigRef: PanelBankSectionConfigRef) =>
  (state: RootState) =>
    state.views.parts[panelBankSectionConfigRef.type][
      panelBankSectionConfigRef.id
    ];

// =============================================================================
// Panel Bank Section Config Property Selectors
// =============================================================================
// Use these to extract individual properties from a panel bank section config. Each uses
// createSelector to memoize the result, so the component only re-renders
// when that specific property changes.

export const makePanelBankSearchSettingsSelector = (
  panelBankConfigRef: PanelBankConfigRef
) =>
  createSelector(
    makePanelBankConfigSelector(panelBankConfigRef),
    panelBankConfig => ({
      autoExpandSearchResults: panelBankConfig.settings.autoExpandSearchResults,
      searchSectionsOpen: panelBankConfig.settings.searchSectionsOpen,
    })
  );

export const makeSectionIsOpenSelector = (
  panelBankSectionConfigRef: PanelBankSectionConfigRef
) =>
  createSelector(
    makePanelBankSectionConfigSelector(panelBankSectionConfigRef),
    panelBankSectionConfig => ({
      __id__: panelBankSectionConfig.__id__,
      isOpen: panelBankSectionConfig.isOpen,
    })
  );
